import { useRef, useState } from 'react';
import { Box, Select, MenuItem, Checkbox, ListItemText, Chip, TextField, FormHelperText } from '@mui/material';
import { CocAnalysisVO } from '../admin-settings/LabRequests';

interface AnalysisSelectProps {
	analyses: CocAnalysisVO[];
	selectedIds: number[];
	otherText: string;
	onChange: (selectedIds: number[]) => void;
	onOtherTextChange: (text: string) => void;
	error?: boolean;
	errorMessage?: string;
	otherError?: boolean;
	width?: number;
}

export default function AnalysisSelect({
	analyses,
	selectedIds,
	otherText,
	onChange,
	onOtherTextChange,
	error,
	errorMessage,
	otherError,
	width = 260,
}: AnalysisSelectProps) {
	const otherAnalysisId = analyses.find((analysis) => analysis.name.trim().toLowerCase() === 'other')?.id;
	const includesOther = otherAnalysisId !== undefined && selectedIds.includes(otherAnalysisId);
	const [open, setOpen] = useState(false);
	const closingRef = useRef(false);

	return (
		<Box width={width}>
			<Select
				multiple
				size="small"
				fullWidth
				displayEmpty
				error={error}
				value={selectedIds}
				open={open}
				onOpen={() => setOpen(true)}
				onClose={() => {
					closingRef.current = true;
					setOpen(false);
					setTimeout(() => { closingRef.current = false; }, 1);
				}}
				onChange={(e) => {
					if (closingRef.current) return;
					onChange(e.target.value as number[]);
				}}
				MenuProps={{
					anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
					transformOrigin: { vertical: 'top', horizontal: 'left' },
					transitionDuration: 0,
				}}
				renderValue={(selected) =>
					(selected as number[]).length === 0
						? 'Select analyses'
						: `${(selected as number[]).length} selected`
				}
			>
				{analyses.map((analysis) => (
					<MenuItem key={analysis.id} value={analysis.id}>
						<Checkbox size="small" checked={selectedIds.includes(analysis.id)} />
						<ListItemText primary={analysis.name} />
					</MenuItem>
				))}
			</Select>
			{error && errorMessage && (
				<FormHelperText error>{errorMessage}</FormHelperText>
			)}
			{selectedIds.length > 0 && (
				<Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
					{selectedIds.map((id) => {
						const analysis = analyses.find((a) => a.id === id);
						return analysis ? (
							<Chip
								key={id}
								label={analysis.name}
								size="small"
								onDelete={() => onChange(selectedIds.filter((sid) => sid !== id))}
								sx={{
									bgcolor: theme => theme.palette.primaryLuBlue100.main,
									color: 'primary.main',
									fontWeight: 500,
									'& .MuiChip-deleteIcon': {
										color: 'primary.main',
										opacity: 0.7,
										'&:hover': { opacity: 1 },
									},
								}}
							/>
						) : null;
					})}
				</Box>
			)}
			{includesOther && (
				<Box mt={0.5}>
					<TextField
						size="small"
						fullWidth
						placeholder="Specify other analysis"
						value={otherText}
						onChange={(e) => e.target.value.length <= 100 && onOtherTextChange(e.target.value)}
						error={otherError || otherText.length === 100}
						helperText={
							otherError
								? 'Specify the "Other" analysis.'
								: otherText.length === 100
								? 'There is a 100 character limit on the analysis field.'
								: undefined
						}
					/>
				</Box>
			)}
		</Box>
	);
}